import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {useEventTracking} from 'src/hooks/useEventTracking';

import Button from '@pb/kit-component-button';
import Group from '@pb/kit-component-group';
import Loading from '@pb/kit-component-loading';
import {Message} from '@pb/kit-component-message';
import Tabs, {Tab} from '@pb/kit-component-tabs-v2';
import Typography from '@pb/kit-component-typography';

import {useDownloadSpMetadata} from '../../hooks/use-download-sp-metadata';
import {useSsoSessionStorage} from '../../hooks/use-sso-session-storage';
import {INITIAL_DOMAIN_DATA, getSelectedDomains} from '../../utils/domain-validation';
import {FieldMappingRow} from '../confirm-login-fields/components/field-mapping-row';
import {StepFooter} from '../step-footer/step-footer';

import {SpConfigInstructions} from './components/sp-config-instructions';

import {
    downloadCard,
    downloadCardDescription,
    downloadRow,
    downloadSpMetadataContainer,
    manualCard,
    manualDescription,
} from './download-sp-metadata.module.styl';

import type {VerifyDomainData} from '../../utils/domain-validation';

type DownloadSpMetadataTab = 'manual' | 'file';

type DownloadSpMetadataProps = {
    onContinue: () => void,
    onBack: () => void,
    saving?: boolean,
};

type SpMetadataSessionData = {
    entityId: string,
    acsUrl: string,
    xmlFile: string,
    domains: string[],
};

const INITIAL_SP_DATA: SpMetadataSessionData = {
    entityId: '',
    acsUrl: '',
    xmlFile: '',
    domains: [],
};

export const DownloadSpMetadata: React.FC<DownloadSpMetadataProps> = ({onContinue, onBack, saving}) => {
    const [domainData] = useSsoSessionStorage<VerifyDomainData>('verifyDomain', INITIAL_DOMAIN_DATA);
    const [spData, setSpData] = useSsoSessionStorage<SpMetadataSessionData>('downloadSpMetadata', INITIAL_SP_DATA);
    const [selectedProvider] = useSsoSessionStorage<string | null>('selectedProvider', null);
    const [activeTab, setActiveTab] = useState<DownloadSpMetadataTab>('manual');
    const {copyConfigurationValue, downloadXml} = useEventTracking();

    const domains = useMemo(() => (
        getSelectedDomains(domainData.domains)
            .map(d => d.value)
            .filter(domain => domain.length > 0)
    ), [domainData.domains]);

    const {fetchMetadata, saveFile, loading, error, data} = useDownloadSpMetadata(domains);

    useEffect(() => {
        const cachedDomains = spData.domains ?? [];
        const domainsChanged = domains.length !== cachedDomains.length
            || domains.some((d, i) => d !== cachedDomains[i]);
        if (spData.xmlFile.length === 0 || domainsChanged) {
            fetchMetadata();
        }
    }, [fetchMetadata, spData.xmlFile, spData.domains, domains]);

    useEffect(() => {
        const cachedDomains = spData.domains ?? [];
        const domainsOutOfSync = domains.length !== cachedDomains.length
            || domains.some((d, i) => d !== cachedDomains[i]);
        if (data && (
            data.entityId !== spData.entityId
            || data.acsUrl !== spData.acsUrl
            || data.xmlFile !== spData.xmlFile
            || domainsOutOfSync
        )) {
            setSpData({
                entityId: data.entityId,
                acsUrl: data.acsUrl,
                xmlFile: data.xmlFile,
                domains,
            });
        }
    }, [data, spData.entityId, spData.acsUrl, spData.xmlFile, spData.domains, setSpData, domains]);

    const entityId = data?.entityId ?? spData.entityId;
    const acsUrl = data?.acsUrl ?? spData.acsUrl;
    const xmlFile = data?.xmlFile ?? spData.xmlFile;
    const hasManualValues = entityId.length > 0 || acsUrl.length > 0;

    const handleTabClick = useCallback((tab: string) => {
        setActiveTab(tab as DownloadSpMetadataTab);
    }, []);

    const handleDownloadXml = useCallback(() => {
        downloadXml();
        saveFile(xmlFile);
    }, [downloadXml, saveFile, xmlFile]);

    return (
        <Group direction="v" gap="l" className={downloadSpMetadataContainer}>
            <Group direction="v" gap="xs">
                <Typography variant="heading-level-1">
                    Download PitchBook SP Metadata
                </Typography>
                <Typography variant="body-level-1">
                    Download our Service Provider metadata and add it to your identity provider.
                </Typography>
            </Group>

            {error != null ? (
                <Message color="error">
                    {error}
                </Message>
            ) : null}

            <Tabs tabsId="download-sp-metadata-tabs" tabPanelId="download-sp-metadata-panel" value={activeTab}>
                <Tab value="manual" caption="Manual Configuration" onClick={() => handleTabClick('manual')}/>
                <Tab value="file" caption="Download File" onClick={() => handleTabClick('file')}/>
            </Tabs>

            <SpConfigInstructions providerId={selectedProvider} tabKey={activeTab}/>

            {activeTab === 'manual' ? (
                <div className={manualCard}>
                    <Group direction="v" gap="xs">
                        <Typography variant="heading-level-3">
                            Manual Configuration Values
                        </Typography>
                        <span className={manualDescription}>
                            Copy and paste these values into your identity provider&apos;s SAML configuration.
                        </span>
                    </Group>

                    {hasManualValues ? (
                        <Group direction="v" gap="m">
                            <FieldMappingRow
                                label="Entity ID"
                                value={entityId}
                                onCopy={() => copyConfigurationValue({field: 'Entity ID'})}
                            />
                            <FieldMappingRow
                                label="ACS URL"
                                value={acsUrl}
                                onCopy={() => copyConfigurationValue({field: 'ACS URL'})}
                            />
                        </Group>
                    ) : (
                        <Typography variant="body-level-1">
                            {loading
                                ? 'Loading configuration values...'
                                : 'Configuration values are not available.'}
                        </Typography>
                    )}
                </div>
            ) : null}

            {activeTab === 'file' ? (
                <div className={downloadCard}>
                    <span className={downloadCardDescription}>
                        This file connects PitchBook to your company&apos;s login system.
                        Upload this file into your login provider.
                    </span>
                    <div className={downloadRow}>
                        <Typography variant="body-level-1">
                            SP Metadata File
                        </Typography>
                        <Button
                            variant="Type.PRIMARY"
                            onClick={handleDownloadXml}
                            disabled={loading || xmlFile.length === 0}>
                            {loading ? <Loading type="ICON"/> : 'Download XML'}
                        </Button>
                    </div>
                </div>
            ) : null}

            <StepFooter
                onContinue={onContinue}
                onBack={onBack}
                busy={saving}
            />
        </Group>
    );
};
